import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import Modal from 'react-modal';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './UserInputRenderer.css';

// Modal styles
const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '500px',
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
  },
};

Modal.setAppElement('#root'); // Set the app element for accessibility

// Custom hook to observe resize of a component
const useResizeObserver = () => {
  const [entry, setEntry] = useState({});
  const [node, setNode] = useState(null);
  const observer = useRef(null);

  const disconnect = useCallback(() => {
    const { current } = observer;
    current && current.disconnect();
  }, []);

  const observe = useCallback(() => {
    const { current } = observer;
    if (node) {
      current.observe(node);
    }
  }, [node]);

  useEffect(() => {
    if (observer.current) {
      disconnect();
    }
    observer.current = new window.ResizeObserver(([ent]) => setEntry(ent));
    observe();
    return () => disconnect();
  }, [node, observe, disconnect]);

  return [setNode, entry];
};


function UserInputRenderer({ pdfFile, template, formData, onFormChange, formFields, setFormFields, handleImageUpload, pageNumber, setNumPages }) {
  const [pageViewports, setPageViewports] = useState({});
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sigCanvas = useRef({});
  const [activeSignatureBoxId, setActiveSignatureBoxId] = useState(null);

  const [containerRef, containerEntry] = useResizeObserver();

  const containerWidth = containerEntry.contentRect ? containerEntry.contentRect.width : 0;

  const onDocumentLoadSuccess = useCallback(async (pdf) => {
    setNumPages(pdf.numPages);
    const loadedFields = [];
    const loadedViewports = {};
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      loadedViewports[i] = page.getViewport({ scale: 1 });
      const annotations = await page.getAnnotations();
      const blacklistRules = template.fieldBlacklist || {};
      const conversionRules = template.fieldTypeConversions || {};

      annotations
        .filter(anno => anno.subtype === 'Widget' && anno.fieldName && !anno.hidden)
        .forEach(anno => {
          const fieldNameLower = anno.fieldName.toLowerCase();
          let finalFieldType = anno.fieldType;

          // Dynamic field type conversion based on template.json
          const txKeywords = conversionRules.Tx || [];
          const photoKeywords = conversionRules.PhotoUpload || [];
          const dateKeywords = conversionRules.Date || [];

          // Priority 1: Tx conversion
          // If a field name matches a Tx keyword, its type becomes 'Tx'.
          // This is to override other potential conversions, e.g., "Photo Legend" should be Tx, not PhotoUpload.
          const isTxOverride = txKeywords.length > 0 && new RegExp(txKeywords.join('|'), 'i').test(fieldNameLower);
          if (isTxOverride) {
            finalFieldType = 'Tx';
          } else {
            // Priority 2: PhotoUpload conversion (only if not a Tx override)
            if (photoKeywords.length > 0) {
              const photoRegex = new RegExp(photoKeywords.join('|'), 'i');
              if (photoRegex.test(fieldNameLower)) {
                finalFieldType = 'PhotoUpload';
              }
            }
          }

          // Priority 3: Date conversion
          // If the resulting field type is 'Tx', check if it should be a 'Date' field.
          if (finalFieldType === 'Tx') {
            if (dateKeywords.length > 0) {
              const dateRegex = new RegExp(dateKeywords.join('|'), 'i');
              if (dateRegex.test(fieldNameLower)) {
                finalFieldType = 'Date';
              }
            }
          }

          const isBlacklisted = (rules, name) => {
            if (!Array.isArray(rules) || rules.length === 0) {
              return false;
            }
            try {
              const regex = new RegExp(rules.join('|'), 'i');
              return regex.test(name);
            } catch (e) {
              console.error(`Invalid regex pattern in blacklist: "${rules.join('|')}"`, e);
              return false;
            }
          };

          if (isBlacklisted(blacklistRules['*'], fieldNameLower) || isBlacklisted(blacklistRules[finalFieldType], fieldNameLower)) {
            return;
          }

          if (finalFieldType === 'Btn' && anno.url) return;
          
          loadedFields.push({
            uniqueId: anno.id,
            fieldName: anno.fieldName,
            type: finalFieldType,
            page: i,
            rect: anno.rect,
            options: anno.options || [],
          });
        });
    }
    setFormFields(loadedFields);
    setPageViewports(loadedViewports);
  }, [setNumPages, template.fieldBlacklist]);

  useEffect(() => {
    if (template && template.boxes) {
      template.boxes.forEach(box => {
        if (box.type === 'text' && box.defaultValue) {
          onFormChange(box.id, { type: 'text-draw', value: box.defaultValue });
        } else if (box.type === 'signature' && !formData[box.id]) {
          onFormChange(box.id, { type: 'signature', value: null });
        }
      });
    }
  }, [template, onFormChange, formData]);

  const openModal = (boxId) => {
    setActiveSignatureBoxId(boxId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setActiveSignatureBoxId(null);
    setIsModalOpen(false);
  };

  const updateSignatureGroup = (value) => {
    if (!activeSignatureBoxId) return;

    const signatureGroups = template.linkedFieldGroups?.signature;
    let targetGroup = null;

    if (signatureGroups) {
      for (const groupName in signatureGroups) {
        if (signatureGroups[groupName].includes(activeSignatureBoxId)) {
          targetGroup = signatureGroups[groupName];
          break;
        }
      }
    }

    if (targetGroup) {
      targetGroup.forEach(boxId => {
        onFormChange(boxId, { type: 'signature', value });
      });
    } else {
      onFormChange(activeSignatureBoxId, { type: 'signature', value });
    }
  };

  const handleSaveSignature = () => {
    const dataUrl = sigCanvas.current.toDataURL();
    updateSignatureGroup(dataUrl);
    closeModal();
  };

  const handleClearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    updateSignatureGroup(null);
    closeModal();
  };

  const handleDrawableTextChange = (id, value) => onFormChange(id, { type: 'text-draw', value });
  const handleFormTextChange = (uniqueId, fieldName, value) => onFormChange(uniqueId, { type: 'text-fill', value, fieldName });
  const handleDropdownChange = (uniqueId, fieldName, value) => onFormChange(uniqueId, { type: 'dropdown', value, fieldName });
  const handleRadioGroupClick = (clickedUniqueId, groupFieldName) => {
    const wasAlreadyChecked = formData[clickedUniqueId]?.value;
    const groupMembers = formFields.filter(f => f.fieldName === groupFieldName && (f.type === 'Btn' || f.type === 'PhotoUpload'));
    
    groupMembers.forEach(member => {
      onFormChange(member.uniqueId, {
        type: 'checkbox',
        value: member.uniqueId === clickedUniqueId ? !wasAlreadyChecked : false,
        fieldName: member.fieldName,
      });
    });
  };

  const onImageFileChange = (e, id, rect = null, page = null) => {
    const file = e.target.files[0];
    if (file) handleImageUpload(id, file, rect, page);
  };

  const boxesForCurrentPage = template.boxes.filter(box => box.page === pageNumber);
  const fieldsForCurrentPage = formFields.filter(field => field.page === pageNumber);
  const photoUploadFields = fieldsForCurrentPage.filter(f => f.type === 'PhotoUpload');
  const otherInteractiveFields = fieldsForCurrentPage.filter(f => f.type !== 'PhotoUpload');

  const originalViewport = pageViewports[pageNumber];
  const scale = originalViewport && containerWidth > 0 ? containerWidth / originalViewport.width : 1;

  return (
    <div className="user-input-container" ref={containerRef}>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Signature Modal"
        overlayClassName="signature-modal-overlay"
      >
        <SignatureCanvas
          ref={sigCanvas}
          penColor='black'
          backgroundColor='rgba(255, 255, 255, 0)'
          canvasProps={{ className: 'signature-canvas-modal' }}
        />
        <div className="modal-buttons">
          <button onClick={handleSaveSignature}>Save</button>
          <button onClick={handleClearSignature}>Clear</button>
          <button onClick={closeModal}>Cancel</button>
        </div>
      </Modal>

      <div className="pdf-render-area">
        {containerWidth > 0 && (
          <Document 
            file={pdfFile} 
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={console.error}
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={containerWidth}
              onRenderSuccess={(page) => setPageDimensions({ width: page.width, height: page.height })}
            />
          </Document>
        )}
        
        {originalViewport && otherInteractiveFields.map(field => {
          const [x1, y1, x2, y2] = field.rect;
          const style = {
            left: `${x1 * scale}px`,
            top: `${pageDimensions.height - (y2 * scale)}px`,
            width: `${(x2 - x1) * scale}px`,
            height: `${(y2 - y1) * scale}px`,
          };

          if (field.type === 'Btn') {
            return (
              <div key={field.uniqueId} style={style} className={`checkbox-overlay interactive-overlay ${formData[field.uniqueId]?.value ? 'checked' : ''}`} onClick={() => handleRadioGroupClick(field.uniqueId, field.fieldName)}>
                {formData[field.uniqueId]?.value && <div className="checkmark">✔</div>}
              </div>
            );
          } else if (field.type === 'Tx') {
            return <textarea key={field.uniqueId} style={style} className="pdf-text-input interactive-overlay" onChange={(e) => handleFormTextChange(field.uniqueId, field.fieldName, e.target.value)} value={formData[field.uniqueId]?.value || ''} />;
          } else if (field.type === 'Date') {
            return <input type="date" key={field.uniqueId} style={style} className="pdf-date-input interactive-overlay" onChange={(e) => handleFormTextChange(field.uniqueId, field.fieldName, e.target.value)} value={formData[field.uniqueId]?.value || ''} />;
          } else if (field.type === 'Ch') {
            return (
              <select key={field.uniqueId} style={style} className="pdf-dropdown-input interactive-overlay" value={formData[field.uniqueId]?.value || ''} onChange={(e) => handleDropdownChange(field.uniqueId, field.fieldName, e.target.value)}>
                <option value="" disabled>Select</option>
                {field.options.map(opt => <option key={opt.exportValue} value={opt.exportValue}>{opt.displayValue}</option>)}
              </select>
            );
          }
          return null;
        })}

        {boxesForCurrentPage.map(box => {
          const style = {
            left: `${box.x * scale}px`,
            top: `${box.y * scale}px`,
            width: `${box.width * scale}px`,
            height: `${box.height * scale}px`,
          };
          if (box.type === 'signature') {
            const signatureDataUrl = formData[box.id]?.value;
            return (
              <div key={box.id} className="input-box signature-box" style={style} onClick={() => openModal(box.id)}>
                {signatureDataUrl ? (
                  <img src={signatureDataUrl} alt="Signature" className="signature-image" />
                ) : (
                  <span>Click to Sign</span>
                )}
              </div>
            );
          }
          return (
            <div key={box.id} className="input-box" style={style}>
              {box.type === 'text' && <textarea placeholder={box.defaultValue || `Enter ${box.id}...`} onChange={(e) => handleDrawableTextChange(box.id, e.target.value)} value={formData[box.id]?.value || ''} />}
              {box.type === 'image' && (
                <div className="image-input-wrapper">
                  {formData[box.id]?.value ? <img src={formData[box.id].value} alt="Preview" className="image-preview" /> : <label htmlFor={`file-input-${box.id}`}>Click to upload</label>}
                  <input id={`file-input-${box.id}`} type="file" accept="image/png, image/jpeg" onChange={(e) => onImageFileChange(e, box.id)} />
                </div>
              )}
            </div>
          );
        })}

        {originalViewport && photoUploadFields.map(field => {
          const [x1, y1, x2, y2] = field.rect;
          const style = {
            left: `${x1 * scale}px`,
            top: `${pageDimensions.height - (y2 * scale)}px`,
            width: `${(x2 - x1) * scale}px`,
            height: `${(y2 - y1) * scale}px`,
          };
          const hasImage = formData[field.uniqueId]?.value;
          return (
            <div key={field.uniqueId} className="input-box interactive-overlay" style={style}>
              <div className={`image-input-wrapper ${hasImage ? 'has-image' : ''}`}>
                {hasImage ? <img src={formData[field.uniqueId].value} alt="Preview" className="image-preview" /> : <label htmlFor={`file-input-${field.uniqueId}`}>Upload Photo</label>}
                <input id={`file-input-${field.uniqueId}`} type="file" accept="image/png, image/jpeg" onChange={(e) => onImageFileChange(e, field.uniqueId, field.rect, field.page, field.fieldName)} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UserInputRenderer;
